// src/components/coin/Coin.tsx
'use client';

import style from './Coin.module.scss';
import { ReactNode, useEffect, useState } from 'react';

type CoinProps = {
  children?: ReactNode;
  /** 基準縮放（XS <576px） */
  scale: number;
  /** 斷點覆寫（可選） */
  scaleSm?: number; // >= 576px
  scaleMd?: number; // >= 768px
  scaleLg?: number; // >= 992px
  scaleXl?: number; // >= 1200px
  scaleXxl?: number; // >= 1400px
  color: 'green' | 'red' | 'yellow';
  className?: string;
};

const BOOTSTRAP_BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

const Coin = ({
  children,
  scale,
  scaleSm,
  scaleMd,
  scaleLg,
  scaleXl,
  scaleXxl,
  color,
  className,
}: CoinProps) => {
  // 依螢幕寬度決定實際 scale
  const [effectiveScale, setEffectiveScale] = useState<number>(scale);

  useEffect(() => {
    const compute = (w: number) => {
      // 由大到小檢查，命中就回傳；否則用基準 scale
      if (w >= BOOTSTRAP_BREAKPOINTS.xxl && scaleXxl != null) return scaleXxl;
      if (w >= BOOTSTRAP_BREAKPOINTS.xl && scaleXl != null) return scaleXl;
      if (w >= BOOTSTRAP_BREAKPOINTS.lg && scaleLg != null) return scaleLg;
      if (w >= BOOTSTRAP_BREAKPOINTS.md && scaleMd != null) return scaleMd;
      if (w >= BOOTSTRAP_BREAKPOINTS.sm && scaleSm != null) return scaleSm;
      return scale;
    };

    // 初始化與監聽 resize
    const apply = () => setEffectiveScale(compute(window.innerWidth));
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [scale, scaleSm, scaleMd, scaleLg, scaleXl, scaleXxl]);

  // 依實際 scale 推導尺寸
  const coinBodySize = 100 * effectiveScale;
  const coinBodyShadow = 6 * effectiveScale;
  const coinDecoSize = 6 * effectiveScale;
  const coinDecoRadius = 43 * effectiveScale;
  const coinContentSize = 1.2 * effectiveScale;

  const coinColor = color === 'green' ? '#5cb2a1' : color === 'red' ? '#ea5848' : '#f0b71a';
  const coinColorShadow = color === 'green' ? '#4a9183' : color === 'red' ? '#b44033' : '#bc8f16';

  return (
    <div
      className={`${style.coinBody} ${className || ''}`}
      style={{
        position: 'relative',
        width: `${coinBodySize}px`,
        height: `${coinBodySize}px`,
        backgroundColor: coinColor,
        boxShadow: `${coinBodyShadow}px 0px 0px 0px ${coinColorShadow}`,
      }}
    >
      {/* <div
        className={`${style.coinBody} ${className || ''}`}
        style={{
          position: 'absolute',
          top: `2px`,
          left: `6px`,
          width: `${coinBodySize}px`,
          height: `${coinBodySize}px`,
          backgroundColor: coinColorShadow,
          zIndex: '-1',
          //boxShadow: `${coinBodyShadow}px 0px 0px 0px ${coinColorShadow}`,
        }}
      /> */}
      {Array.from({ length: 12 }, (_, index) => {
        const angle = index * 30;
        return (
          <div
            key={index}
            className={`${style.coinDeco} ${style[`coinDeco${index + 1}`]}`}
            style={{
              width: `${coinDecoSize}px`,
              height: `${coinDecoSize}px`,
              transform: `
                translate(-50%, -50%)
                rotate(${angle}deg)
                translateY(-${coinDecoRadius}px)
                rotate(-${angle}deg)
              `,
            }}
          />
        );
      })}
      <div
        className={style.coinContent}
        style={{ transform: `translate(-50%, -50%) scale(${coinContentSize})` }}
      >
        {children}
      </div>
    </div>
  );
};

export default Coin;
